/**
 * Main App Bar Component
 * Top navigation bar with capability-driven menu, user info, and logout.
 * Menu groups and ordering are driven by useMenuConfig (no hardcoded arrays).
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery,
  ListItemIcon,
  ListItemText,
  Collapse,
  List,
  ListItemButton,
  Drawer,
  Badge,
} from '@mui/material';
import {
  Logout,
  Person,
  Menu as MenuIcon,
  ExpandMore,
  ExpandLess,
  Lock as LockIcon,
  DarkMode,
  LightMode,
  HelpOutline,
  CardGiftcard,
  SupportAgent,
} from '@mui/icons-material';
import { useAuth } from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';
import { useMenuConfig } from '../../hooks/useMenuConfig';
import { getMenuIcon } from '../../config/menuIcons';
import CompanySelector from './CompanySelector';
import BrandLogo from '../common/BrandLogo';
import { useThemeMode, usePermissions } from '../../hooks';
import { useSupportUnreadStore } from '../../stores/supportUnreadStore';
import Tooltip from '@mui/material/Tooltip';

const MainAppBar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const menuGroups = useMenuConfig();
  const { mode, toggleMode } = useThemeMode();
  const { hasPermission } = usePermissions();
  const podeVerAtendimento = hasPermission('support_conversation.view');
  const naoLidas = useSupportUnreadStore((state) => state.count);
  const initSupportUnread = useSupportUnreadStore((state) => state.init);

  useEffect(() => {
    if (podeVerAtendimento) initSupportUnread();
  }, [podeVerAtendimento, initSupportUnread]);

  // Generic anchor state for desktop dropdowns
  const [anchors, setAnchors] = useState<Record<string, HTMLElement | null>>({});
  // Mobile collapse state
  const [mobileCollapses, setMobileCollapses] = useState<Set<string>>(new Set());
  // User menu + mobile menu
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  // Mobile navigation lives in a left-anchored Drawer (md breakpoint and below).
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const openAnchor = useCallback((id: string, el: HTMLElement) => {
    setAnchors((prev) => ({ ...prev, [id]: el }));
  }, []);

  const closeAnchor = useCallback((id: string) => {
    setAnchors((prev) => ({ ...prev, [id]: null }));
  }, []);

  const closeAllAnchors = useCallback(() => {
    setAnchors({});
  }, []);

  const toggleMobileCollapse = useCallback((id: string) => {
    setMobileCollapses((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleMobileMenuClose = useCallback(() => {
    setMobileDrawerOpen(false);
    setMobileCollapses(new Set());
  }, []);

  const handleNavigation = useCallback(
    (path: string) => {
      navigate(path);
      closeAllAnchors();
      handleMobileMenuClose();
    },
    [navigate, closeAllAnchors, handleMobileMenuClose]
  );

  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      setUserMenuAnchorEl(null);
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      setLogoutLoading(false);
    }
  };

  const getUserTypeLabel = (userType: string) => {
    switch (userType) {
      case 'admin':
        return 'Administrador';
      case 'profissional':
        return 'Profissional';
      default:
        return userType;
    }
  };

  return (
    <AppBar position="static" elevation={1}>
      <Toolbar>
        {/* Logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
          onClick={() => navigate('/')}
        >
          <BrandLogo
            variant="light"
            size="sm"
            sx={{ height: 36, mr: 3 }}
          />
        </Box>

        {/* Desktop Navigation */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            {menuGroups.map((group) => {
              const GroupIcon = getMenuIcon(group.iconName);
              return (
                <React.Fragment key={group.id}>
                  <Button
                    color="inherit"
                    startIcon={<GroupIcon />}
                    endIcon={<ExpandMore />}
                    onClick={(e) => openAnchor(group.id, e.currentTarget)}
                  >
                    {group.label}
                  </Button>
                  <Menu
                    anchorEl={anchors[group.id] ?? null}
                    open={Boolean(anchors[group.id])}
                    onClose={() => closeAnchor(group.id)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{ sx: { minWidth: 220 } }}
                  >
                    {group.items.flatMap((item) => {
                      const ItemIcon = getMenuIcon(item.iconName);
                      const elements: React.ReactNode[] = [];
                      if (item.dividerBefore) {
                        elements.push(<Divider key={`div-${item.id}`} />);
                      }
                      elements.push(
                        <MenuItem
                          key={item.id}
                          onClick={() => handleNavigation(item.path)}
                          disabled={item.disabled}
                        >
                          <ListItemIcon>
                            <ItemIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>{item.label}</ListItemText>
                        </MenuItem>
                      );
                      return elements;
                    })}
                  </Menu>
                </React.Fragment>
              );
            })}
          </Box>
        )}

        {/* Mobile Menu Button — opens left-anchored Drawer */}
        {isMobile && (
          <Box sx={{ flexGrow: 1 }}>
            <IconButton
              color="inherit"
              onClick={() => setMobileDrawerOpen(true)}
              edge="start"
              aria-label="Abrir menu de navegação"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        {/* Company Selector — desktop only; on mobile it lives inside the Drawer */}
        {!isMobile && <CompanySelector />}

        {/* Mobile Navigation Drawer (left-anchored, md breakpoint and below) */}
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={handleMobileMenuClose}
          PaperProps={{ sx: { width: 280, display: 'flex', flexDirection: 'column' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
            <BrandLogo variant="light" size="sm" sx={{ height: 32 }} />
          </Box>
          <Divider />
          {/* Company selector at the top of the drawer (mobile-only placement) */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <CompanySelector />
          </Box>
          <Divider />
          <List component="nav" sx={{ flexGrow: 1, overflowY: 'auto' }}>
            {menuGroups.flatMap((group) => {
              const GroupIcon = getMenuIcon(group.iconName);
              const isOpen = mobileCollapses.has(group.id);
              const elements: React.ReactNode[] = [];
              elements.push(
                <ListItemButton key={group.id} onClick={() => toggleMobileCollapse(group.id)}>
                  <ListItemIcon>
                    <GroupIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={group.label} />
                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              );
              elements.push(
                <Collapse key={`collapse-${group.id}`} in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {group.items.flatMap((item) => {
                      const ItemIcon = getMenuIcon(item.iconName);
                      const els: React.ReactNode[] = [];
                      if (item.dividerBefore) {
                        els.push(<Divider key={`mob-div-${item.id}`} sx={{ ml: 2 }} />);
                      }
                      els.push(
                        <ListItemButton
                          key={item.id}
                          sx={{ pl: 4 }}
                          onClick={() => handleNavigation(item.path)}
                          disabled={item.disabled}
                        >
                          <ListItemIcon>
                            <ItemIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={item.label} />
                        </ListItemButton>
                      );
                      return els;
                    })}
                  </List>
                </Collapse>
              );
              return elements;
            })}
          </List>
          {/* Theme toggle pinned to the bottom-left of the drawer (mobile-only placement) */}
          <Divider />
          <Box sx={{ px: 1, py: 1, display: 'flex', justifyContent: 'flex-start' }}>
            <IconButton
              onClick={toggleMode}
              aria-label={mode === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'}
            >
              {mode === 'dark' ? <DarkMode fontSize="small" /> : <LightMode fontSize="small" />}
            </IconButton>
          </Box>
        </Drawer>

        {/* Central de Atendimento — desktop only; visível só para quem tem a permissão */}
        {!isMobile && podeVerAtendimento && (
          <Tooltip title={naoLidas > 0 ? `${naoLidas} mensagem(ns) nova(s)` : 'Central de Atendimento'}>
            <IconButton
              color="inherit"
              onClick={() => navigate('/administrativo/atendimento')}
              aria-label="Central de Atendimento"
              sx={{ mx: 0.5 }}
            >
              <Badge badgeContent={naoLidas} color="error" max={99}>
                <SupportAgent fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        )}

        {/* Theme Toggle — desktop only; on mobile it lives inside the Drawer */}
        {!isMobile && (
          <IconButton
            color="inherit"
            onClick={toggleMode}
            aria-label={mode === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'}
            sx={{ mx: 0.5 }}
          >
            {mode === 'dark' ? <DarkMode fontSize="small" /> : <LightMode fontSize="small" />}
          </IconButton>
        )}

        {/* User Info and Menu */}
        <Box
          onClick={(e) => setUserMenuAnchorEl(e.currentTarget)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            cursor: 'pointer',
            borderLeft: '1px solid rgba(255, 255, 255, 0.5)',
            px: 2,
            py: 0.75,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderColor: 'rgba(255, 255, 255, 0.8)',
            },
          }}
          aria-label="account of current user"
          aria-controls="user-menu"
          aria-haspopup="true"
        >
          <Typography variant="body2" fontWeight={500} color="inherit">
            {user?.name?.split(' ')[0] || 'Usuario'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.2 }}
          >
            {getUserTypeLabel(user?.userType || '')}
          </Typography>
        </Box>

        <Menu
          id="user-menu"
          anchorEl={userMenuAnchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          keepMounted
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={Boolean(userMenuAnchorEl)}
          onClose={() => setUserMenuAnchorEl(null)}
        >
          <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" noWrap>
              {user?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem
            onClick={() => {
              setUserMenuAnchorEl(null);
              handleNavigation('/profile');
            }}
          >
            <ListItemIcon>
              <Person fontSize="small" />
            </ListItemIcon>
            <ListItemText>Meu Perfil</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setUserMenuAnchorEl(null);
              handleNavigation('/change-password');
            }}
          >
            <ListItemIcon>
              <LockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Alterar Senha</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setUserMenuAnchorEl(null);
              handleNavigation('/ajuda');
            }}
          >
            <ListItemIcon>
              <HelpOutline fontSize="small" />
            </ListItemIcon>
            <ListItemText>Central de Ajuda</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setUserMenuAnchorEl(null);
              window.open(
                import.meta.env['VITE_OPASS_URL'] || 'https://clube.liderasst.com.br',
                '_blank',
                'noopener,noreferrer'
              );
            }}
          >
            <ListItemIcon>
              <CardGiftcard fontSize="small" />
            </ListItemIcon>
            <ListItemText>Clube Lidera</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} disabled={logoutLoading}>
            <ListItemIcon>
              {logoutLoading ? (
                <CircularProgress size={16} />
              ) : (
                <Logout fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>Sair</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default MainAppBar;
